import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  MEMBERSHIP_ROUTES,
  membershipClockResponseSchema,
  splitTimeLeft,
  type MembershipClock,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";

export const membershipKeys = {
  mine: ["me", "membership"] as const,
};

/** El reloj de recompra de la sesión. Solo lectura: lo mueve el servidor. */
export function useMyMembership() {
  return useQuery({
    queryKey: membershipKeys.mine,
    queryFn: () => authFetch(MEMBERSHIP_ROUTES.mine, membershipClockResponseSchema, { baseUrl: "" }),
    // Se vuelve a pedir cada minuto: si el ciclo vence con la pestaña
    // abierta, el estado cambia solo (la cuenta regresiva ya lo enseña
    // en cero, pero el badge y el CTA salen del servidor).
    refetchInterval: 60_000,
  });
}

/**
 * La cuenta regresiva, anclada al reloj del SERVIDOR.
 *
 * `serverNow` vino con la respuesta; la diferencia con el reloj local se
 * guarda una vez y cada latido la aplica. Así un reloj de máquina
 * atrasado no enseña días de más, y uno adelantado no vence a nadie
 * antes de tiempo. El latido va a 1 s porque aquí SÍ se pintan segundos.
 */
export function useMembershipCountdown(clock: MembershipClock | undefined) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!clock?.validUntil) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [clock?.validUntil]);

  // El desfase se mide UNA vez por respuesta (cuando `serverNow` cambia),
  // no en cada render: medirlo en cada latido lo haría crecer con el
  // tiempo transcurrido y la cuenta se congelaría.
  const offsetMs = useMemo(
    () => (clock ? new Date(clock.serverNow).getTime() - Date.now() : 0),
    [clock?.serverNow], // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (!clock) return splitTimeLeft(null, new Date(now));
  const deadline = clock.validUntil ? new Date(clock.validUntil) : null;
  return splitTimeLeft(deadline, new Date(now + offsetMs));
}
